import React from 'react';
import { Star, ExternalLink, Loader2 } from 'lucide-react';
import { useGooglePlaces } from '@/hooks/useGooglePlaces';

interface GoogleRatingProps {
  siteName: string;
  address: string;
  city: string;
  className?: string;
}

const GoogleRating: React.FC<GoogleRatingProps> = ({ siteName, address, city, className = '' }) => {
  console.log('GoogleRating component rendered with:', { siteName, address, city });
  const { data, loading, error } = useGooglePlaces({ siteName, address, city });
  
  console.log('GoogleRating state:', { data, loading, error });

  if (loading) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Chargement des avis...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <span className="text-sm text-red-500">Erreur: {error}</span>
      </div>
    );
  }

  if (!data.rating) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <span className="text-sm text-muted-foreground">Aucune note Google trouvée</span>
      </div>
    );
  }

  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <div key={i} className="relative w-4 h-4">
            <Star className="w-4 h-4 text-gray-300 absolute" />
            <div className="overflow-hidden w-1/2">
              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
            </div>
          </div>
        );
      } else {
        stars.push(
          <Star key={i} className="w-4 h-4 text-gray-300" />
        );
      }
    }

    return stars;
  };

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className="flex items-center gap-1">
        <div className="flex">
          {renderStars(data.rating)}
        </div>
        <span className="text-sm font-medium text-foreground ml-1">
          {data.rating.toFixed(1)}
        </span>
      </div>
      
      <span className="text-sm text-muted-foreground">
        ({data.reviewCount} avis)
      </span>

      {data.googleUrl && (
        <a
          href={data.googleUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 text-sm text-primary hover:text-primary/80 transition-colors"
        >
          <span>Voir sur Google</span>
          <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </div>
  );
};

export default GoogleRating;